import i18n from "i18next"
import { initReactI18next } from "react-i18next"

// Translation resources
const mandatoryFieldsText = {
  en: "You must complete the fields marked with an asterisk (<red>*</red>).",
  fi: "Sinun tulee täyttää tähdellä (<red>*</red>) merkityt kentät.",
  se: "Frågor med stjärna (<red>*</red>) är obligatoriska.",
}

const enTranslation = {
  pageTitle: "Customer Feedback | Terveystalo",
  pageTitleInvoicing: "Invoicing and Payment Feedback | Terveystalo",
  pageTitleOrg: "Feedback from Organizations | Terveystalo",
  pageTitleVaaratapahtuma: "Patient Adverse Event | Terveystalo",
  pageTitleKorjauspyynto: "Personal Data Correction Request | Terveystalo",
  pageTitleMuistutus: "Patient Act Reminder | Terveystalo",
  login: "Authenticate",
  logout: "Sign out",
  close: "Close",
  welcome: "Welcome to Feedback Form",
  submit: "Submit",
  loading: "Loading user...",
  toimipaikkaNames: {
    "Ajanvaraus ja Asiakaspalvelu": "Ajanvaraus ja Asiakaspalvelu",
    "Ajanvaraus ja asiakaspalvelu": "Ajanvaraus ja Asiakaspalvelu",
    "Terveystalo Digiterveys": "Terveystalo Digiterveys",
    "Terveystalo Digiterveys Digihoitajatiimit": "Terveystalo Digiterveys Digihoitajatiimit",
    "Terveystalo Digiterveys Hoitajatiimit": "Terveystalo Digiterveys Hoitajatiimit",
    Yleinen: "Yleinen",
  },
  routes: {
    customer: {
      canonical: "customer",
      aliases: ["feedback", "asiakas", "palaute", "kund", "respons"],
    },
    invoicing: {
      canonical: "invoicing",
      aliases: ["laskutus", "fakturering"],
    },
    org: {
      canonical: "org",
      aliases: ["organization", "organisaatio", "yritys", "organisation", "foretag", "yhtio"],
    },
    vaaratapahtuma: {
      canonical: "vaaratapahtuma",
      aliases: ["adverse-event", "odeslig-handelse"],
    },
    korjauspyynto: {
      canonical: "correction-request",
      aliases: ["data-correction", "korjauspyynto", "korrigeringsbegaran"],
    },
    muistutus: {
      canonical: "patient-reminder",
      aliases: ["muistutus", "patientpaminnelse"],
    },
    fallbackUrl: "https://www.terveystalo.com/en/customer/customer-feedback",
  },
  error: {
    feedbackOnBehalfOfRequired: "Please select on whose behalf you are giving feedback.",
    onBehalfOfNameRequired:
      "Please enter the name of the person on whose behalf you are giving feedback.",
    feedbackTypeRequired: "Error: Feedback type is required.",
    submitFailed: "Failed to submit feedback",
    generic: "Error",
    notAuthenticated: "Please log in to submit feedback.",
    notAuthenticatedInvoicing: "Please log in to send inquiries and feedback on invoicing.",
    notAuthenticatedVaaratapahtuma: "Please log in to report a patient adverse event.",
    notAuthenticatedKorjauspyynto: "Please log in to submit a personal data correction request.",
    notAuthenticatedMuistutus: "Please log in to submit a patient act reminder.",
    patientNameRequired: "Please enter the patient's name.",
    patientSsnRequired: "Please enter the patient's personal identity code.",
    patientPhoneRequired: "Please enter the patient's phone number.",
    patientEmailRequired: "Please enter the patient's email address.",
    patientAddressRequired: "Please enter the patient's postal address.",
    representativeNameRequired: "Please enter the representative's name.",
    representativeSsnRequired: "Please enter the representative's personal identity code.",
    representativePhoneRequired: "Please enter the representative's phone number.",
    representativeEmailRequired: "Please enter the representative's email address.",
    representativeAddressRequired: "Please enter the representative's postal address.",
    responseMethodRequired: "Please select how you want to receive the response.",
    muistutusOrFileRequired: "Please enter the reminder text or attach a file.",
    fileRequiredOnBehalf:
      "Please attach a power of attorney document when acting on behalf of another person.",
    requestTypeRequired: "Please select at least one request type.",
    authenticationFailed: "Authentication failed. Please log in again.",
    nameRequired: "Please enter your name.",
    emailRequired: "Please enter your email address.",
    phoneRequired: "Please enter your phone number.",
    typeRequired: "Error: Type is required.",
    descriptionRequired: "Please enter your message.",
    visitDateRequired: "Please enter the visit date.",
    subjectRequired: "Please select a subject.",
    toimipaikkaRequired: "Please select a location.",
    wishToReceiveReplyRequired: "Please select whether you wish to receive a reply.",
    preferredContactMethodRequired: "Please select how you would like to receive your reply.",
    apiErrorTitle: "Service Unavailable",
    apiErrorMessage:
      "We're experiencing technical difficulties. Please contact support for assistance.",
    close: "Close",
  },
  success: {
    feedbackSubmitted: "Feedback submitted successfully!",
    korjauspyyntoSubmitted: "Personal data correction request submitted successfully!",
    muistutusSubmitted: "Patient act reminder submitted successfully!",
  },
  feedbackOnBehalfOf: {
    select: "Please select...",
    myself: "On my own behalf",
    my_child: "On behalf of my child",
    my_relative: "On behalf of my relative",
  },
  feedbackType: {
    select: "Select feedback type...",
    suggestion: "Suggestion",
    thank_you: "Thank you",
    complaint: "Complaint",
  },
  feedbackIam: {
    select: "Please select...",
    public_sector: "Public sector or community customer",
    kela65: "Kela65",
    commitment_to_pay: "Commitment to pay customer",
    purchase_service_authorization: "Purchase service authorization customer",
    service_voucher: "Service voucher customer",
    sickness_fund: "Member of a sickness fund",
    occupational_health_care: "Occupational health care customer",
    insurance: "Insurance customer",
    private: "Private customer",
  },
  feedbackSubject: {
    select: "Select feedback subject...",
    appointment_booking: "Appointment booking",
    customer_service: "Customer service",
    arrival_to_appointment: "Arrival to appointment",
    quality_of_care: "Quality of care",
    examination: "Examination (e.g. laboratory, X-ray, screening)",
    billing_or_payment: "Billing or payment",
    pricing_or_campaigns: "Pricing or campaigns",
    online_store: "Online store",
    data_protection_and_security: "Data protection and security",
    remote_services: "Remote services",
    premises: "Premises",
    applications_or_electronic_services: "Applications or electronic services",
    marketing_and_communication:
      "Marketing and communication (newsletters, terveystalo.com website)",
    environmental_responsibility: "Environmental responsibility",
    other: "Other",
  },
  feedbackService: {
    select: "Please select...",
    customer_service_point:
      "Service at customer service point (waiting, queuing, guidance, registration, signs)",
    doctor_appointment: "Doctor's appointment",
    nurse_appointment: "Nurse's appointment",
    other_specialist_appointment:
      "Other specialist appointment (psychologist, physiotherapist, massage therapist, etc.)",
    laboratory_services: "Laboratory services",
    procedure_or_surgery: "Procedure or surgery",
    imaging: "Imaging (X-ray, MRI, ultrasound, screening)",
    oral_health: "Oral health",
    occupational_health_services: "Occupational health services",
    remote_appointment_or_digital_services: "Remote appointment or digital services",
    rehabilitation_services: "Rehabilitation services",
    other_service:
      "Other service (service provided by Terveystalo's partner, online store services)",
    terveystalo_app: "Terveystalo app",
    terveystalo_website: "www.terveystalo.com website",
  },
  wishToReceiveReply: {
    select: "Please select...",
    yes: "Yes",
    no: "No",
  },
  preferredContactMethod: {
    select: "Please select...",
    phone: "Phone",
    email: "Email",
  },
  authModal: {
    title: "Authentication Required",
    message: "To receive a reply to your feedback, you need to authenticate.",
    authenticate: "Authenticate",
    cancel: "Cancel",
  },
  fileUploadAuthModal: {
    title: "Authentication Required",
    message: "To add file attachments, you need to authenticate.",
    authenticate: "Authenticate",
    cancel: "Cancel",
  },
  form: {
    title: "Customer feedback",
    guideText:
      "To receive a reply to your feedback, you must authenticate in the section <boldItalic>I wish to receive a reply to my feedback</boldItalic>.",
    lead: mandatoryFieldsText.en,
    feedbackInfo: "Feedback information",
    contactInfo: "Contact information",
    givingFeedback: "I am giving feedback:",
    onBehalfOfName: "Name of the person:",
    onBehalfOfNamePlaceholder: "Enter the person's name",
    myFeedback: "My feedback is:",
    name: "Name:",
    namePlaceholder: "Enter your name",
    email: "Email:",
    emailPlaceholder: "Enter your email address",
    phone: "Phone:",
    phonePlaceholder: "Enter your phone number (e.g. 0401234567)",
    iam: "I am:",

    employer: "Employer:",
    subject: "The matter concerns:",
    subjectOther: "Other, what:",
    service: "Specify by selecting the service the feedback concerns:",
    dateOfOccurrence: "Date of occurrence:",
    selectDate: "Select date",
    describeMatter: "Describe the matter more fully:",
    feedback: "Feedback / message:",
    replyWish: "I wish to receive a reply to my feedback:",
    preferredContactMethod: "How would you like to receive your reply:",
    toimipaikka: "Location the feedback concerns:",
    toimipaikkaSelect: "Select location...",
    toimipaikkaPlaceholder: "Type to search locations...",
    toimipaikkaHelp: [
      "If your feedback concerns Appointment booking or Customer service, select {{appointmentBooking}}.",
      "If your feedback concerns Terveystalo remote services (e.g. doctor chat), select {{digitalHealth}}.",
      "If your feedback concerns the Digital nurse service (e.g. nurse and mental health chat), select {{nurseTeams}}.",
      "If your feedback concerns Terveystalo in general, select {{general}}.",
    ],
    privacyNotice:
      "Your personal data is stored in Terveystalo's personal data register, where it is processed in accordance with the ",
    privacyStatement: "Privacy statement",
    privacyStatementUrl: "https://www.terveystalo.com/en/privacy-statement/private-customer",
    privacyCustomers: " for private customers.",
    submitting: "Submitting...",
    attachments: "Add attachment here:",
    addAttachment: "Add attachment",
    filesSelected: "{{count}} file(s) selected",
    fileUploadHint: "Max attachment size 20MB",
    loading: "Loading...",
  },
  auth: {
    error: "Authentication failed. Please try again.",
    processing: "Authenticating...",
  },
  invoicingForm: {
    title: "Personal customer invoicing and feedback form",
    lead: mandatoryFieldsText.en,
    formInfo: "Feedback information",
    typeLabel: "Type:",
    typeInstructions:
      "If you have a question or want to give feedback about a received invoice or payment, select Feedback type. If you want general advice about invoicing and payment, select Inquiry type.",
    descriptionLabel: "Describe the matter more fully (feedback/message):",
    visitDateLabel: "Visit date:",
    invoiceNumberLabel: "Invoice number:",
    emailLabel: "Email address:",
    type: {
      select: "Select the type of feedback",
      feedback: "Feedback",
      thank_you: "Thank you",
      inquiry: "Inquiry",
    },
  },
  organizationForm: {
    title: "Feedback from corporations and organizations",
    lead: mandatoryFieldsText.en,
    formInfo: "Organizational information",
    feedbackInfo: "Feedback information",
    organizationTypeLabel: "Organization type:",
    organizationNameLabel: "Organization name:",
    businessIdLabel: "Business ID:",
    businessIdPlaceholder: "Enter the business ID in format 1234567-8",
    customerRelationshipLabel: "Type of customer relationship:",
    subjectLabel: "Select a subject you wish to give feedback on:",
    invoiceNumberLabel: "Invoice number:",
    menuItemLabel:
      "Select a menu item to specify where the matter occurred or what it is related to:",
    dateOfOccurrenceLabel: "Date of occurrence:",
    descriptionLabel: "Describe the matter more fully (feedback/message):",
    wishToReceiveResponseLabel: "I wish to receive a reply to my Feedback:",
    toimipaikkaLabel: "Select the location your feedback concerns:",
    toimipaikkaSelect: "Select location...",
    toimipaikkaPlaceholder: "Type to search locations...",
    nameLabel: "My name is:",
    namePlaceholder: "Enter your name",
    emailLabel: "Email address:",
    organizationType: {
      select: "Select organization type...",
      municipality: "Municipality",
      other: "Other",
      wellbeing_area: "Wellbeing area",
      insurance_company: "Insurance company",
      community: "Community",
      company: "Company",
    },
    customerRelationship: {
      select: "Select customer relationship...",
      hansel_occupational: "Hansel occupational healthcare customer",
      public_sector: "Public sector customer or a public body",
      local_occupational: "Local occupational healthcare customer",
      sickness_fund: "Sickness or insurance fund",
      insurance: "Insurance customer",
      national_occupational: "National occupational healthcare customer",
    },
    subject: {
      select: "Select subject...",
      appointment_booking: "Appointment booking",
      customer_experience: "Customer experience",
      customer_relationship_management: "Customer relationship management",
      remote_consultation: "Remote consultation (chat, video, phone)",
      extranet_service: "Extranet service",
      direct_access_physical_therapist: "Direct access to a physical therapist",
      pricing: "Pricing",
      treatment_and_examinations: "Treatment and examinations",
      invoicing: "Invoicing",
      medical_applications: "Medical applications (Health surveys Sante/Etydi)",
      marketing_and_communication: "Marketing and communication",
      other: "Other",
      sales: "Sales",
      supply_of_services: "Supply of services",
      sirius: "Sirius",
      agreement_issues: "Agreement issues",
      electronically_submitted_employee_info: "Electronically submitted employee information",
      oma_terveys_app: "Oma Terveys/ Terveystalo app",
      referrals_made_at_terveystalo: "Referrals made at Terveystalo",
      data_protection: "Data protection",
      website: "www.terveystalo.com website",
      environmental_responsibility: "Environmental responsibility",
    },
    menuItem: {
      select: "Select menu item...",
      customer_service_desk: "Service at the customer service desk",
      laboratory_examinations: "Laboratory examinations",
      surgery_and_operations: "Surgery and operations",
      imaging_examinations: "Imaging examinations",
      screening: "Screening",
      oral_health: "Oral health",
      partner_service: "Service provided by Terveystalo's partner",
      appointment: "Appointment (doctor, nurse, physiotherapist etc.)",
      video_consultation: "Video consultation",
      common: "Common",
    },
  },
  vaaratapahtumaForm: {
    title: "Patient adverse event report",
    lead: mandatoryFieldsText.en,
    formInfo: "Event information",
    descriptionLabel:
      "Please describe as accurately as possible what happened and what the consequences were:",
    toimipaikkaLabel: "Select the location the matter concerns:",
    toimipaikkaSelect: "Select location...",
    toimipaikkaPlaceholder: "Type to search locations...",
    dateOfOccurrenceLabel: "Date of occurrence:",
    emailLabel: "Email address:",
  },
  korjauspyyntoForm: {
    title: "Personal data correction request",
    lead: mandatoryFieldsText.en,
    formInfo: "Request information",
    gdprNotice:
      "Based on the EU General Data Protection Regulation (EU 2016/679), I request that inaccurate and incorrect personal data concerning me (the data subject) stored in your Personal Customers' personal data register be corrected and/or unnecessary personal data be deleted.",
    notAuthenticatedMessage: "You must be logged in to submit a personal data correction request.",
    printModeEnabled: "Printable form mode",
    printModeBanner:
      "This form will be printed. Fill in all required fields, print, sign, and submit in person.",
    printButton: "Print Form",
    printInstructions:
      "Print this form, sign it, and submit it to a Terveystalo location with valid identification.",
    usePrintableForm: "Use printable form",
    printModeExplanation:
      "If you do not wish to log in, you can use the printable form. Fill out the form, print it, sign it, and submit it to a Terveystalo location.",
    authenticatedExplanation: "When logged in, you can submit your correction request immediately.",
    firstAndLastName: "First and last name:",
    personalIdentityCode: "Personal identity code:",
    dateField: "Date:",
    signature: "Signature:",
    employeeSignature: "Terveystalo employee signature and name clarification:",
    identityVerificationTitle:
      "Requester's identity verified (To be filled by Terveystalo representative)",
    identityTypePassport: "Valid passport",
    identityTypeDriversLicense: "Finnish driver's license",
    identityTypeIdCard: "Official ID card (issued by police)",
    identityTypeOther: "Other, what:",
    dateReceived: "Date received:",
    decision: "Decision (To be filled by Terveystalo representative):",
    submissionInstructions:
      "Submit this form to any Terveystalo location with valid identification.",
    toimipaikkaLabel: "Location the request concerns:",
    toimipaikkaSelect: "Select location...",
    toimipaikkaPlaceholder: "Type to search locations...",
    requestTypesLabel: "I request that the following information be:",
    requestTypePoistettaviksi: "Deleted",
    requestTypeMuutettaviksi: "Modified",
    requestTypeLisattaviksi: "Added",
    requestTypeOstopalveluasiakkaana:
      "The correction request concerns my visits to Terveystalo as a purchased service customer (e.g. <i>hyvinvointialue</i>, employer's own occupational health care, or another healthcare service provider's payment commitment, service voucher, or purchase service authorization).",
    descriptionPoistettaviksiLabel: "Describe the information to be deleted:",
    descriptionMuutettaviksiLabel: "Describe the information to be modified:",
    descriptionLisattaviksiLabel: "Describe the information to be added:",
    descriptionOstopalveluasiakkaanaLabel: "Describe the matter regarding purchased services:",
    addressLabel: "Address:",
    phoneNumberLabel: "Phone number:",
    emailLabel: "Email address:",
    dateOfRequestLabel: "Date of request:",
  },
  muistutusForm: {
    title: "Patient Act Reminder",
    lead: mandatoryFieldsText.en,
    formInfo: "Reminder information",
    authenticatedExplanation: "When authenticated, you can submit your reminder immediately.",
    notAuthenticatedMessage: "You must be authenticated to submit a patient act reminder.",
    printModeExplanation:
      "If you do not wish to authenticate, you can use the printable form. Fill out the form, print it, sign it, and submit it to a Terveystalo location.",
    usePrintableForm: "Use printable form",
    printModeBanner:
      "This form will be printed. Fill in all required fields, print, sign, and submit in person.",
    printInstructions:
      "Print this form, sign it, and submit it to a Terveystalo location with valid identification.",
    printButton: "Print Form",
    onBehalfOfLabel: "Are you acting on behalf of another person?",
    onBehalfOfYes: "Yes",
    onBehalfOfNo: "No",
    onBehalfOfInfoTitle: "Acting on behalf of another person",
    onBehalfOfInfoText:
      "Please remember to attach a power of attorney for acting on behalf of another person.",
    readMore: "Read more:",
    onBehalfOfInfoLink: "Acting on behalf of another person | Terveystalo",
    onBehalfOfInfoUrl:
      "https://www.terveystalo.com/fi/asiakaspalvelu/asiointiohjeet/toisen-puolesta-asiointi",
    patientInfoTitle: "Patient information",
    patientNameLabel: "Name:",
    patientNamePlaceholder: "Enter the patient's name",
    patientSsnLabel: "Personal identity code:",
    patientSsnPlaceholder: "Enter the patient's personal identity code",
    patientPhoneLabel: "Phone number:",
    patientPhonePlaceholder: "Enter the patient's phone number",
    patientEmailLabel: "Email:",
    patientEmailPlaceholder: "Enter the patient's email address",
    patientAddressLabel: "Postal address:",
    patientAddressPlaceholder: "Enter the patient's postal address",
    purchaseServiceCustomerLabel:
      "The reminder concerns my visit to Terveystalo as a purchased service customer (e.g. wellbeing area, employer's own occupational health care, or another healthcare service provider's payment commitment, service voucher, or purchase service authorization).",
    representativeInfoTitle: "Representative information",
    representativeNameLabel: "Name:",
    representativeNamePlaceholder: "Enter the representative's name",
    representativeSsnLabel: "Personal identity code:",
    representativeSsnPlaceholder: "Enter the representative's personal identity code",
    representativePhoneLabel: "Phone number:",
    representativePhonePlaceholder: "Enter the representative's phone number",
    representativeEmailLabel: "Email:",
    representativeEmailPlaceholder: "Enter the representative's email address",
    representativeAddressLabel: "Postal address:",
    representativeAddressPlaceholder: "Enter the representative's postal address",
    reminderDetailsTitle: "Reminder details",
    toimipaikkaLabel: "Location:",
    toimipaikkaSelect: "Select location...",
    toimipaikkaPlaceholder: "Type to search locations...",
    muistutusLabel: "Reminder:",
    muistutusPlaceholder:
      "Enter the details of the reminder in the text field or attach as a separate attachment.",
    responseMethodTitle: "How do you want to receive the response:",
    responseMethodEncryptedEmail: "Delivered by encrypted email",
    responseMethodPost: "Delivered by post",
    responseMethodApp: "Delivered to Terveystalo app/web service (does not apply to oral health)",
    responseMethodAppDisabled: "Not available when acting on behalf of another person",
  },
} as const

type Translations<T> = {
  [P in keyof T]: T[P] extends object ? Translations<T[P]> : string
}

const fiTranslation: Translations<typeof enTranslation> = {
  pageTitle: "Henkilöasiakkaan palaute | Terveystalo",
  pageTitleInvoicing: "Laskutuksen ja maksamisen palaute | Terveystalo",
  pageTitleOrg: "Palaute Yritykseltä | Terveystalo",
  pageTitleVaaratapahtuma: "Potilaan vaaratapahtuma | Terveystalo",
  pageTitleKorjauspyynto: "Henkilötietojen korjauspyyntö | Terveystalo",
  pageTitleMuistutus: "Potilaslain mukainen muistutus | Terveystalo",
  login: "Tunnistaudu",
  logout: "Poistu",
  close: "Sulje",
  welcome: "Tervetuloa palautelomakkeeseen",
  submit: "Lähetä",
  loading: "Ladataan käyttäjää...",
  toimipaikkaNames: {
    "Ajanvaraus ja Asiakaspalvelu": "Ajanvaraus ja Asiakaspalvelu",
    "Ajanvaraus ja asiakaspalvelu": "Ajanvaraus ja Asiakaspalvelu",
    "Terveystalo Digiterveys": "Terveystalo Digiterveys",
    "Terveystalo Digiterveys Digihoitajatiimit": "Terveystalo Digiterveys Digihoitajatiimit",
    "Terveystalo Digiterveys Hoitajatiimit": "Terveystalo Digiterveys Hoitajatiimit",
    Yleinen: "Yleinen",
  },
  routes: {
    customer: {
      canonical: "asiakas",
      aliases: ["customer", "feedback", "palaute", "kund", "respons"],
    },
    invoicing: {
      canonical: "laskutus",
      aliases: ["invoicing", "fakturering"],
    },
    org: {
      canonical: "org",
      aliases: ["organization", "organisaatio", "yritys", "organisation", "foretag", "yhtio"],
    },
    vaaratapahtuma: {
      canonical: "vaaratapahtuma",
      aliases: ["adverse-event", "odeslig-handelse"],
    },
    korjauspyynto: {
      canonical: "korjauspyynto",
      aliases: ["correction-request", "data-correction", "korrigeringsbegaran"],
    },
    muistutus: {
      canonical: "muistutus",
      aliases: ["patient-reminder", "patientpaminnelse"],
    },
    fallbackUrl: "https://www.terveystalo.com/fi/asiakkaalle/palaute",
  },
  error: {
    feedbackOnBehalfOfRequired: "Valitse kenen puolesta annat palautetta.",
    onBehalfOfNameRequired: "Anna sen henkilön nimi, jonka puolesta annat palautetta.",
    feedbackTypeRequired: "Virhe: Palautetyyppi vaaditaan.",
    submitFailed: "Palautteen lähetys epäonnistui",
    generic: "Virhe",
    notAuthenticated: "Tunnistaudu lähettääksesi palautetta.",
    notAuthenticatedInvoicing:
      "Tunnistaudu lähettääksesi tiedusteluja ja palautetta laskutuksesta.",
    notAuthenticatedVaaratapahtuma: "Tunnistaudu ilmoittaaksesi vaaratapahtumasta.",
    notAuthenticatedKorjauspyynto: "Tunnistaudu lähettääksesi henkilötietojen korjauspyynnön.",
    notAuthenticatedMuistutus: "Tunnistaudu lähettääksesi potilaslain mukaisen muistutuksen.",
    patientNameRequired: "Anna potilaan nimi.",
    patientSsnRequired: "Anna potilaan henkilötunnus.",
    patientPhoneRequired: "Anna potilaan puhelinnumero.",
    patientEmailRequired: "Anna potilaan sähköpostiosoite.",
    patientAddressRequired: "Anna potilaan postiosoite.",
    representativeNameRequired: "Anna puolesta asioivan nimi.",
    representativeSsnRequired: "Anna puolesta asioivan henkilötunnus.",
    representativePhoneRequired: "Anna puolesta asioivan puhelinnumero.",
    representativeEmailRequired: "Anna puolesta asioivan sähköpostiosoite.",
    representativeAddressRequired: "Anna puolesta asioivan postiosoite.",
    responseMethodRequired: "Valitse miten haluat vastauksen.",
    muistutusOrFileRequired: "Kirjoita muistutuksen tiedot tai liitä tiedosto.",
    fileRequiredOnBehalf: "Liitä valtakirja puolesta asiointiin.",
    requestTypeRequired: "Valitse vähintään yksi pyyntötyyppi.",
    authenticationFailed: "Tunnistautuminen epäonnistui. Tunnistaudu uudelleen.",
    nameRequired: "Anna nimesi.",
    emailRequired: "Anna sähköpostiosoitteesi.",
    phoneRequired: "Anna puhelinnumerosi.",
    typeRequired: "Virhe: Tyyppi vaaditaan.",
    descriptionRequired: "Ole hyvä ja kirjoita viestisi.",
    visitDateRequired: "Anna käyntipäivä.",
    subjectRequired: "Valitse aihe.",
    toimipaikkaRequired: "Valitse toimipaikka.",
    wishToReceiveReplyRequired: "Valitse haluatko vastauksen palautteeseen.",
    preferredContactMethodRequired: "Valitse miten haluat vastaanottaa vastauksen.",
    apiErrorTitle: "Palvelu ei ole käytettävissä",
    apiErrorMessage:
      "Palautejärjestelmässä on tapahtunut tekninen virhe. Yritä myöhemmin uudelleen.",
    close: "Sulje",
  },
  success: {
    feedbackSubmitted: "Palaute lähetetty onnistuneesti!",
    korjauspyyntoSubmitted: "Korjauspyyntö lähetetty onnistuneesti!",
    muistutusSubmitted: "Muistutus lähetetty onnistuneesti!",
  },
  feedbackOnBehalfOf: {
    select: "Valitse...",
    myself: "Omasta puolestani",
    my_child: "Lapseni puolesta",
    my_relative: "Omaiseni puolesta",
  },
  feedbackType: {
    select: "Valitse palautetyyppi...",
    suggestion: "Ehdotus",
    thank_you: "Kiitos",
    complaint: "Valitus",
  },
  feedbackIam: {
    select: "Valitse...",
    public_sector: "Julkisen sektorin tai yhteisön asiakas",
    kela65: "Kela65",
    commitment_to_pay: "Maksusitoumusasiakas",
    purchase_service_authorization: "Ostopalveluvaltuutusasiakas",
    service_voucher: "Palveluseteliasiakas",
    sickness_fund: "Sairauskassan jäsen",
    occupational_health_care: "Työterveysasiakas",
    insurance: "Vakuutusasiakas",
    private: "Yksityisasiakas",
  },
  feedbackSubject: {
    select: "Valitse palautteen aihe...",
    appointment_booking: "Ajanvaraus",
    customer_service: "Asiakaspalvelu",
    arrival_to_appointment: "Saapuminen vastaanotolle",
    quality_of_care: "Hoidon laatu",
    examination: "Tutkimus (esim. laboratorio, röntgen, seulonta)",
    billing_or_payment: "Laskutus tai maksaminen",
    pricing_or_campaigns: "Hinnoittelu tai kampanjat",
    online_store: "Verkkokauppa",
    data_protection_and_security: "Tietosuoja ja tietoturva",
    remote_services: "Etäasiointi",
    premises: "Toimitilat",
    applications_or_electronic_services: "Sovellukset tai sähköiset palvelut",
    marketing_and_communication:
      "Markkinointi ja viestintä (uutiskirjeet, terveystalo.com-sivusto)",
    environmental_responsibility: "Ympäristövastuullisuus",
    other: "Muu",
  },
  feedbackService: {
    select: "Valitse...",
    customer_service_point:
      "Asiointi asiakaspalvelupisteessä (odottaminen, jonotus, ohjaus, ilmoittautuminen, opasteet)",
    doctor_appointment: "Lääkärin vastaanotto",
    nurse_appointment: "Hoitajan vastaanotto",
    other_specialist_appointment:
      "Muun asiantuntijan vastaanotto (psykologi, fysioterapeutti, hieroja jne)",
    laboratory_services: "Laboratoriopalvelut",
    procedure_or_surgery: "Toimenpide tai leikkaus",
    imaging: "Kuvantaminen (röntgen, magneetti, ultraääni, seulonta)",
    oral_health: "Suun terveydenhuolto",
    occupational_health_services: "Työterveyspalvelut",
    remote_appointment_or_digital_services: "Etävastaanotto tai digipalvelut",
    rehabilitation_services: "Kuntoutuspalvelut",
    other_service:
      "Muu palvelu (Terveystalon kumppanin toteuttama palvelu, verkkokaupan tarjoamat palvelut jne.)",
    terveystalo_app: "Terveystalo sovellus",
    terveystalo_website: "www.terveystalo.com -sivut",
  },
  wishToReceiveReply: {
    select: "Valitse...",
    yes: "Kyllä",
    no: "Ei",
  },
  preferredContactMethod: {
    select: "Valitse...",
    phone: "Puhelin",
    email: "Sähköposti",
  },
  authModal: {
    title: "Tunnistautuminen vaaditaan",
    message: "Saadaksesi vastauksen palautteeseesi, sinun tulee tunnistautua.",
    authenticate: "Tunnistaudu",
    cancel: "Peruuta",
  },
  fileUploadAuthModal: {
    title: "Tunnistautuminen vaaditaan",
    message: "Liitetiedostojen lisääminen vaatii tunnistautumisen.",
    authenticate: "Tunnistaudu",
    cancel: "Peruuta",
  },
  form: {
    title: "Henkilöasiakkaan palaute",
    guideText:
      "Mikäli haluat vastauksen palautteeseesi, sinun tulee tunnistautua kohdassa <boldItalic>Haluan vastauksen palautteeseen</boldItalic>.",
    lead: mandatoryFieldsText.fi,
    feedbackInfo: "Palautteen tiedot",
    contactInfo: "Yhteystietoni",
    givingFeedback: "Annan palautetta:",
    onBehalfOfName: "Henkilön nimi:",
    onBehalfOfNamePlaceholder: "Syötä henkilön nimi",
    myFeedback: "Palautteeni on:",
    name: "Nimi:",
    namePlaceholder: "Syötä nimesi",
    email: "Sähköposti:",
    emailPlaceholder: "Syötä sähköpostiosoitteesi",
    phone: "Puhelin:",
    phonePlaceholder: "Kirjoita puhelinnumero muodossa 0401234567",
    iam: "Olen:",

    employer: "Työnantaja:",
    subject: "Asia koskee:",
    subjectOther: "Muuta, mitä:",
    service: "Tarkenna valitsemalla palvelu, jota palaute koskee:",
    dateOfOccurrence: "Tapahtuman päivämäärä:",
    selectDate: "Valitse päivämäärä",
    describeMatter: "Kerro asiasta tarkemmin:",
    feedback: "Palaute / viesti:",
    replyWish: "Haluan vastauksen palautteeseen:",
    preferredContactMethod: "Miten haluat vastaanottaa vastauksen:",
    toimipaikka: "Toimipaikka, jota palautteeni koskee:",
    toimipaikkaSelect: "Valitse toimipaikka...",
    toimipaikkaPlaceholder: "Kirjoita hakeaksesi toimipaikkoja...",
    toimipaikkaHelp: [
      "Jos palautteesi koskee Ajanvarausta tai Asiakaspalvelukeskusta, valitse toimipaikaksi {{appointmentBooking}}",
      "Jos palautteesi koskee Terveystalon etäpalveluita (esim. lääkäri-chat), valitse toimipaikaksi {{digitalHealth}}.",
      "Jos palautteesi koskee Digihoitajapalvelua (esim. hoitaja- ja mielen-chat), valitse toimipaikaksi {{nurseTeams}}",
      "Jos palautteesi koskee Terveystaloa yleisesti, valitse toimipaikaksi {{general}}.",
    ],
    privacyNotice:
      "Henkilötietosi tallennetaan Terveystalon henkilörekisteriin, jossa niitä käsitellään ",
    privacyStatement: "Henkilöasiakkaiden tietosuojaselosteen",
    privacyStatementUrl: "https://www.terveystalo.com/fi/tietosuojaseloste/henkiloasiakkaat",
    privacyCustomers: " mukaisesti.",
    submitting: "Lähetetään...",
    attachments: "Lisää tästä liitetiedosto:",
    addAttachment: "Lisää liitetiedosto",
    filesSelected: "{{count}} tiedosto(a) valittu",
    fileUploadHint: "Liitetiedosto saa olla kooltaan enintään 20 MB",
    loading: "Ladataan...",
  },
  auth: {
    error: "Tunnistautuminen epäonnistui. Yritä uudelleen.",
    processing: "Tunnistaudutaan...",
  },
  invoicingForm: {
    title: "Henkilöasiakkaan laskutuksen ja maksamisen palautelomake",
    lead: mandatoryFieldsText.fi,
    formInfo: "Palautteen tiedot",
    typeLabel: "Tyyppi:",
    typeInstructions:
      "Kun sinulla on kysyttävää tai haluat antaa palautetta saapuneeseen laskuun tai maksuun liittyen, valitse Palaute tyyppi. Kun haluat yleistä neuvontaa laskutukseen ja maksamiseen, valitse Tiedustelu tyyppi.",
    descriptionLabel: "Kerro asiasta tarkemmin (palaute/viesti):",
    visitDateLabel: "Käyntipäivä:",
    invoiceNumberLabel: "Laskun numero:",
    emailLabel: "Sähköpostiosoite:",
    type: {
      select: "Valitse palautteen tyyppi",
      feedback: "Palaute",
      thank_you: "Kiitos",
      inquiry: "Tiedustelu",
    },
  },
  organizationForm: {
    title: "Asiakasyritysten, yhteisöjen ja julkisen sektorin organisaatioiden palaute",
    lead: mandatoryFieldsText.fi,
    formInfo: "Organisaation tiedot",
    feedbackInfo: "Palautteen tiedot",
    organizationTypeLabel: "Organisaatiotyyppi:",
    organizationNameLabel: "Organisaation nimi:",
    businessIdLabel: "Y-tunnus:",
    businessIdPlaceholder: "Syötä Y-tunnus muodossa 1234567-8",
    customerRelationshipLabel: "Asiakassuhteen tyyppi:",
    subjectLabel: "Valitse aihe, josta haluat antaa palautetta:",
    invoiceNumberLabel: "Laskun numero:",
    menuItemLabel:
      "Valitse valikkovaihtoehto täsmentääksesi, missä asia tapahtui tai mihin se liittyy:",
    dateOfOccurrenceLabel: "Tapahtuman päivämäärä:",
    descriptionLabel: "Kerro asiasta tarkemmin (palaute/viesti):",
    wishToReceiveResponseLabel: "Haluan saada palautteeseeni vastauksen:",
    toimipaikkaLabel: "Valitse toimipaikka, jota palautteesi koskee:",
    toimipaikkaSelect: "Valitse toimipaikka...",
    toimipaikkaPlaceholder: "Kirjoita hakeaksesi toimipaikkoja...",
    nameLabel: "Nimeni on:",
    namePlaceholder: "Syötä nimesi",
    emailLabel: "Sähköpostiosoite:",
    organizationType: {
      select: "Valitse organisaatiotyyppi...",
      municipality: "Kunta",
      other: "Muu",
      wellbeing_area: "Hyvinvointialueet",
      insurance_company: "Vakuutusyhtiö",
      community: "Yhteisö",
      company: "Yritys",
    },
    customerRelationship: {
      select: "Valitse asiakassuhde...",
      hansel_occupational: "Hansel työterveysasiakas",
      public_sector: "Julkisen sektorin asiakas tai julkinen toimija",
      local_occupational: "Paikallinen työterveysasiakas",
      sickness_fund: "Sairauskassa tai vakuutuskassa",
      insurance: "Vakuutusasiakas",
      national_occupational: "Valtakunnallinen työterveysasiakas",
    },
    subject: {
      select: "Valitse aihe...",
      appointment_booking: "Ajanvaraus",
      customer_experience: "Asiakaskokemus",
      customer_relationship_management: "Asiakkuudenhallinta",
      remote_consultation: "Etäkonsultaatio (chat, video, puhelin)",
      extranet_service: "Extranet-palvelu",
      direct_access_physical_therapist: "Suora yhteys fysioterapeuttiin",
      pricing: "Hinnoittelu",
      treatment_and_examinations: "Hoito ja tutkimukset",
      invoicing: "Laskutus",
      medical_applications: "Lääketieteelliset sovellukset (Sante/Etydi kyselyt)",
      marketing_and_communication: "Markkinointi ja viestintä",
      other: "Muu",
      sales: "Myynti",
      supply_of_services: "Palvelujen toimitus",
      sirius: "Sirius",
      agreement_issues: "Sopimukseen liittyvät asiat",
      electronically_submitted_employee_info: "Sähköisesti toimitetut työntekijätiedot",
      oma_terveys_app: "Oma Terveys/ Terveystalo-sovellus",
      referrals_made_at_terveystalo: "Terveystalon tekemät lähetteet",
      data_protection: "Tietosuoja",
      website: "www.terveystalo.com verkkosivusto",
      environmental_responsibility: "Ympäristövastuu",
    },
    menuItem: {
      select: "Valitse valikkovaihtoehto...",
      customer_service_desk: "Palvelu asiakaspalvelupisteessä",
      laboratory_examinations: "Laboratoriotutkimukset",
      surgery_and_operations: "Leikkaukset ja toimenpiteet",
      imaging_examinations: "Kuvantamistutkimukset",
      screening: "Seulonta",
      oral_health: "Suun terveys",
      partner_service: "Terveystalon kumppanin tarjoama palvelu",
      appointment: "Vastaanottoaika (lääkäri, hoitaja, fysioterapeutti jne.)",
      video_consultation: "Videokonsultaatio",
      common: "Yleinen",
    },
  },
  vaaratapahtumaForm: {
    title: "Potilaan vaaratapahtuma",
    lead: mandatoryFieldsText.fi,
    formInfo: "Tapahtuman tiedot",
    descriptionLabel: "Kerro mahdollisimman tarkasti mitä tapahtui ja mitkä olivat seuraukset:",
    toimipaikkaLabel: "Toimipaikka, jota asia koskee:",
    toimipaikkaSelect: "Valitse toimipaikka...",
    toimipaikkaPlaceholder: "Kirjoita hakeaksesi toimipaikkoja...",
    dateOfOccurrenceLabel: "Tapahtuman päivämäärä:",
    emailLabel: "Sähköpostiosoite:",
  },
  korjauspyyntoForm: {
    title: "Henkilötietojen korjauspyyntö",
    lead: mandatoryFieldsText.fi,
    formInfo: "Pyynnön tiedot",
    gdprNotice:
      "Pyydän EU:n yleisen tietosuoja-asetuksen (EU 2016/679) perusteella, että ylläpitämäänne Henkilöasiakkaiden henkilörekisteriin tallennetut, minua (rekisteröity) koskevat epätarkat ja virheelliset henkilötiedot korjataan ja/tai tarpeettomat henkilötiedot poistetaan.",
    notAuthenticatedMessage:
      "Sinun tulee olla tunnistautuneena lähettääksesi henkilötietojen korjauspyynnön.",
    printModeEnabled: "Tulostettava lomake -tila",
    printModeBanner:
      "Tämä lomake tulostetaan. Täytä kaikki tarvittavat kentät, tulosta, allekirjoita ja toimita henkilökohtaisesti.",
    printButton: "Tulosta lomake",
    printInstructions:
      "Tulosta tämä lomake, allekirjoita se ja toimita se Terveystalon toimipisteeseen voimassa olevan henkilöllisyystodistuksen kanssa.",
    usePrintableForm: "Käytä tulostettavaa lomaketta",
    printModeExplanation:
      "Jos et halua tunnistautua, voit käyttää tulostettavaa lomaketta. Täytä lomake, tulosta se, allekirjoita ja toimita Terveystalon toimipisteeseen.",
    authenticatedExplanation: "Tunnistautuneena voit lähettää korjauspyynnön välittömästi.",
    firstAndLastName: "Etu- ja sukunimi:",
    personalIdentityCode: "Henkilötunnus:",
    dateField: "Päiväys:",
    signature: "Allekirjoitus:",
    employeeSignature: "Terveystalon työntekijän allekirjoitus ja nimenselvennys:",
    identityVerificationTitle:
      "Pyynnön tekijän henkilöllisyys tarkastettu (Terveystalon edustaja täyttää)",
    identityTypePassport: "Passi (voimassa oleva)",
    identityTypeDriversLicense: "Ajokortti (suomalainen)",
    identityTypeIdCard: "Virallinen henkilökortti (poliisin myöntämä)",
    identityTypeOther: "Muu mikä:",
    dateReceived: "Päiväys (lomake vastaanotettu):",
    decision: "Päätös (Terveystalon edustaja täyttää):",
    submissionInstructions:
      "Toimita tämä lomake mihin tahansa Terveystalon toimipisteeseen voimassa olevan henkilöllisyystodistuksen kanssa.",
    toimipaikkaLabel: "Toimipaikka, jota pyyntö koskee:",
    toimipaikkaSelect: "Valitse toimipaikka...",
    toimipaikkaPlaceholder: "Kirjoita hakeaksesi toimipaikkoja...",
    requestTypesLabel: "Pyydän, että seuraavat tiedot:",
    requestTypePoistettaviksi: "Poistetaan",
    requestTypeMuutettaviksi: "Muutetaan",
    requestTypeLisattaviksi: "Lisätään",
    requestTypeOstopalveluasiakkaana:
      "Korjauspyyntö koskee asiointiani Terveystalossa ostopalveluasiakkaana (esim. hyvinvointialueen, työnantajan oman työterveyshuollon tai toisen terveydenhuollon palvelunjärjestäjän maksusitoumus, palveluseteli tai ostopalveluvaltuutus).",
    descriptionPoistettaviksiLabel: "Kuvaa poistettavat tiedot:",
    descriptionMuutettaviksiLabel: "Kuvaa muutettavat tiedot:",
    descriptionLisattaviksiLabel: "Kuvaa lisättävät tiedot:",
    descriptionOstopalveluasiakkaanaLabel: "Kuvaa ostopalveluasiakkaana koskeva asia:",
    addressLabel: "Osoite:",
    phoneNumberLabel: "Puhelinnumero:",
    emailLabel: "Sähköpostiosoite:",
    dateOfRequestLabel: "Pyynnön päivämäärä:",
  },
  muistutusForm: {
    title: "Potilaslain mukainen muistutus",
    lead: mandatoryFieldsText.fi,
    formInfo: "Muistutuksen tiedot",
    authenticatedExplanation: "Tunnistautuneena voit lähettää muistutuksen välittömästi.",
    notAuthenticatedMessage:
      "Sinun tulee olla tunnistautuneena lähettääksesi potilaslain mukaisen muistutuksen.",
    printModeExplanation:
      "Jos et halua tunnistautua, voit käyttää tulostettavaa lomaketta. Täytä lomake, tulosta se, allekirjoita ja toimita Terveystalon toimipisteeseen.",
    usePrintableForm: "Käytä tulostettavaa lomaketta",
    printModeBanner:
      "Tämä lomake tulostetaan. Täytä kaikki tarvittavat kentät, tulosta, allekirjoita ja toimita henkilökohtaisesti.",
    printInstructions:
      "Tulosta tämä lomake, allekirjoita se ja toimita se Terveystalon toimipisteeseen voimassa olevan henkilöllisyystodistuksen kanssa.",
    printButton: "Tulosta lomake",
    onBehalfOfLabel: "Asioitko toisen puolesta?",
    onBehalfOfYes: "Kyllä",
    onBehalfOfNo: "Ei",
    onBehalfOfInfoTitle: "Toisen puolesta asiointi",
    onBehalfOfInfoText: "Muistathan liittää valtakirjan puolesta asiointiin.",
    readMore: "Lue lisää:",
    onBehalfOfInfoLink: "Toisen puolesta asiointi | Terveystalo",
    onBehalfOfInfoUrl:
      "https://www.terveystalo.com/fi/asiakaspalvelu/asiointiohjeet/toisen-puolesta-asiointi",
    patientInfoTitle: "Potilaan tiedot",
    patientNameLabel: "Nimi:",
    patientNamePlaceholder: "Syötä potilaan nimi",
    patientSsnLabel: "Henkilötunnus:",
    patientSsnPlaceholder: "Syötä potilaan henkilötunnus",
    patientPhoneLabel: "Puhelinnumero:",
    patientPhonePlaceholder: "Syötä potilaan puhelinnumero",
    patientEmailLabel: "Sähköposti:",
    patientEmailPlaceholder: "Syötä potilaan sähköpostiosoite",
    patientAddressLabel: "Postiosoite:",
    patientAddressPlaceholder: "Syötä potilaan postiosoite",
    purchaseServiceCustomerLabel:
      "Muistutus koskee asiointiani Terveystalossa ostopalveluasiakkaana (esim. hyvinvointialueen, työnantajan oman työterveyshuollon tai toisen terveydenhuollon palvelunjärjestäjän maksusitoumus, palveluseteli tai ostopalveluvaltuutus).",
    representativeInfoTitle: "Puolesta asioivan tiedot",
    representativeNameLabel: "Nimi:",
    representativeNamePlaceholder: "Syötä puolesta asioivan nimi",
    representativeSsnLabel: "Henkilötunnus:",
    representativeSsnPlaceholder: "Syötä puolesta asioivan henkilötunnus",
    representativePhoneLabel: "Puhelinnumero:",
    representativePhonePlaceholder: "Syötä puolesta asioivan puhelinnumero",
    representativeEmailLabel: "Sähköposti:",
    representativeEmailPlaceholder: "Syötä puolesta asioivan sähköpostiosoite",
    representativeAddressLabel: "Postiosoite:",
    representativeAddressPlaceholder: "Syötä puolesta asioivan postiosoite",
    reminderDetailsTitle: "Muistutuksen tiedot",
    toimipaikkaLabel: "Toimipaikka:",
    toimipaikkaSelect: "Valitse toimipaikka...",
    toimipaikkaPlaceholder: "Kirjoita hakeaksesi toimipaikkoja...",
    muistutusLabel: "Muistutus:",
    muistutusPlaceholder:
      "Kirjaa muistutuksen tiedot tekstikenttään tai liitä erillisenä liitteenä.",
    responseMethodTitle: "Miten haluat vastauksen:",
    responseMethodEncryptedEmail: "Toimitetaan salatulla sähköpostilla",
    responseMethodPost: "Toimitetaan postitse",
    responseMethodApp:
      "Toimitetaan Terveystalo-sovellukseen/verkkopalveluun (ei koske suun terveyttä)",
    responseMethodAppDisabled: "Ei valittavissa puolesta asioitaessa",
  },
}

const seTranslation: Translations<typeof enTranslation> = {
  pageTitle: "Kundrespons | Terveystalo",
  pageTitleInvoicing: "Fakturering och betalning respons | Terveystalo",
  pageTitleOrg: "Respons från organisationer | Terveystalo",
  pageTitleVaaratapahtuma: "Patientens odesliga händelse | Terveystalo",
  pageTitleKorjauspyynto: "Korrigeringsbegäran för personuppgifter | Terveystalo",
  pageTitleMuistutus: "Anmärkning enligt patientlagen | Terveystalo",
  login: "Autentisera",
  logout: "Logga ut",
  close: "Stäng",
  welcome: "Välkommen till feedback formuläret",
  submit: "Skicka",
  loading: "Laddar användare...",
  toimipaikkaNames: {
    "Ajanvaraus ja Asiakaspalvelu": "Ajanvaraus ja Asiakaspalvelu",
    "Ajanvaraus ja asiakaspalvelu": "Ajanvaraus ja Asiakaspalvelu",
    "Terveystalo Digiterveys": "Terveystalo Digiterveys",
    "Terveystalo Digiterveys Digihoitajatiimit": "Terveystalo Digiterveys Digihoitajatiimit",
    "Terveystalo Digiterveys Hoitajatiimit": "Terveystalo Digiterveys Hoitajatiimit",
    Yleinen: "Yleinen",
  },
  routes: {
    customer: {
      canonical: "kund",
      aliases: ["customer", "feedback", "respons", "asiakas", "palaute"],
    },
    invoicing: {
      canonical: "fakturering",
      aliases: ["invoicing", "laskutus"],
    },
    org: {
      canonical: "org",
      aliases: ["organization", "organisaatio", "yritys", "organisation", "foretag", "yhtio"],
    },
    vaaratapahtuma: {
      canonical: "vaaratapahtuma",
      aliases: ["adverse-event", "odeslig-handelse"],
    },
    korjauspyynto: {
      canonical: "korrigeringsbegaran",
      aliases: ["correction-request", "data-correction", "korjauspyynto"],
    },
    muistutus: {
      canonical: "patientpaminnelse",
      aliases: ["patient-reminder", "muistutus"],
    },
    fallbackUrl: "https://www.terveystalo.com/sv/kund/kundrespons",
  },
  error: {
    feedbackOnBehalfOfRequired: "Välj på vems vägnar du ger respons.",
    onBehalfOfNameRequired: "Ange namnet på personen på vars vägnar du ger respons.",
    feedbackTypeRequired: "Fel: Feedbacktyp krävs.",
    submitFailed: "Feedbackinlämning misslyckades",
    generic: "Fel",
    notAuthenticated: "Autentisera dig för att skicka feedback.",
    notAuthenticatedInvoicing:
      "Autentisera dig för att skicka förfrågningar och respons om fakturering.",
    notAuthenticatedVaaratapahtuma:
      "Autentisera dig för att rapportera en patientens odesliga händelse.",
    notAuthenticatedKorjauspyynto:
      "Autentisera dig för att skicka en korrigeringsbegäran för personuppgifter.",
    notAuthenticatedMuistutus: "Autentisera dig för att skicka en anmärkning enligt patientlagen.",
    patientNameRequired: "Ange patientens namn.",
    patientSsnRequired: "Ange patientens personbeteckning.",
    patientPhoneRequired: "Ange patientens telefonnummer.",
    patientEmailRequired: "Ange patientens e-postadress.",
    patientAddressRequired: "Ange patientens postadress.",
    representativeNameRequired: "Ange ombud namn.",
    representativeSsnRequired: "Ange ombud personbeteckning.",
    representativePhoneRequired: "Ange ombud telefonnummer.",
    representativeEmailRequired: "Ange ombud e-postadress.",
    representativeAddressRequired: "Ange ombud postadress.",
    responseMethodRequired: "Välj hur du vill få svaret.",
    muistutusOrFileRequired: "Ange anmärkningens innehåll eller bifoga en fil.",
    fileRequiredOnBehalf: "Bifoga en fullmakt för ombud.",
    requestTypeRequired: "Välj minst en begärantyp.",
    authenticationFailed: "Autentisering misslyckades. Autentisera dig igen.",
    nameRequired: "Ange ditt namn.",
    emailRequired: "Ange din e-postadress.",
    phoneRequired: "Ange ditt telefonnummer.",
    typeRequired: "Fel: Typ krävs.",
    descriptionRequired: "Var vänlig och skriv ditt meddelande.",
    visitDateRequired: "Ange besöksdatum.",
    subjectRequired: "Välj ett ämne.",
    toimipaikkaRequired: "Välj en plats.",
    wishToReceiveReplyRequired: "Välj om du vill ha svar på din respons.",
    preferredContactMethodRequired: "Välj hur du vill få ditt svar.",
    apiErrorTitle: "Tjänsten är inte tillgänglig",
    apiErrorMessage: "Vi upplever tekniska svårigheter. Vänligen kontakta support för hjälp.",
    close: "Stäng",
  },
  success: {
    feedbackSubmitted: "Feedbacken har skickats!",
    korjauspyyntoSubmitted: "Korrigeringsbegäran har skickats!",
    muistutusSubmitted: "Anmärkning enligt patientlagen har skickats!",
  },
  feedbackOnBehalfOf: {
    select: "Välja...",
    myself: "På mitt eget vägnar",
    my_child: "På mitt barns vägnar",
    my_relative: "På min anhörigs vägnar",
  },
  feedbackType: {
    select: "Välj feedbacktyp...",
    suggestion: "Förslag",
    thank_you: "Tack",
    complaint: "Klagomål",
  },
  feedbackIam: {
    select: "Välja...",
    public_sector: "Kund från en organisation inom offentliga sektorn eller ett samfund",
    kela65: "Kela65",
    commitment_to_pay: "Betalningsförbindelsekund",
    purchase_service_authorization: "Kund med auktorisering för inköpstjänster",
    service_voucher: "Kund med servicesedel",
    sickness_fund: "Medlem i sjukkassa",
    occupational_health_care: "Företagshälsovårdskund",
    insurance: "Försäkringskund",
    private: "Privatkund",
  },
  feedbackSubject: {
    select: "Välj ärende...",
    appointment_booking: "Tidsbeställning",
    customer_service: "Kundservice",
    arrival_to_appointment: "Ankomst till mottagningen",
    quality_of_care: "Vårdkvalitet",
    examination: "Undersökning (t.ex. laboratorium, röntgen, screening)",
    billing_or_payment: "Fakturering eller betalning",
    pricing_or_campaigns: "Prissättning eller kampanjer",
    online_store: "Webbshop",
    data_protection_and_security: "Integritetsskydd och säkerhet",
    remote_services: "Distanstjänster",
    premises: "Lokaler",
    applications_or_electronic_services: "Applikationer eller elektroniska tjänster",
    marketing_and_communication:
      "Marknadsföring och kommunikation (nyhetsbrev, terveystalo.com-webbplats)",
    environmental_responsibility: "Miljöansvar",
    other: "Annat",
  },
  feedbackService: {
    select: "Välja...",
    customer_service_point:
      "Service vid kundservicepunkt (väntan, köande, vägledning, registrering, skyltar)",
    doctor_appointment: "Läkarmottagning",
    nurse_appointment: "Sjuksköterskemottagning",
    other_specialist_appointment:
      "Annan specialistmottagning (psykolog, fysioterapeut, massageterapeut, etc.)",
    laboratory_services: "Laboratorietjänster",
    procedure_or_surgery: "Åtgärd eller operation",
    imaging: "Bilddiagnostik (röntgen, MR, ultraljud, screening)",
    oral_health: "Munhälsa",
    occupational_health_services: "Företagshälsovårdstjänster",
    remote_appointment_or_digital_services: "Distansmottagning eller digitala tjänster",
    rehabilitation_services: "Rehabiliteringstjänster",
    other_service:
      "Annan tjänst (tjänst som tillhandahålls av Terveystalos partner, webbshopens tjänster, etc.)",
    terveystalo_app: "Terveystalo-appen",
    terveystalo_website: "www.terveystalo.com webbplats",
  },
  wishToReceiveReply: {
    select: "Välja...",
    yes: "Ja",
    no: "Nej",
  },
  preferredContactMethod: {
    select: "Välja...",
    phone: "Telefon",
    email: "E-post",
  },
  authModal: {
    title: "Autentisering krävs",
    message: "För att få svar på din respons måste du autentisera dig.",
    authenticate: "Autentisera",
    cancel: "Avbryt",
  },
  fileUploadAuthModal: {
    title: "Autentisering krävs",
    message: "För att lägga till bilagor måste du autentisera dig.",
    authenticate: "Autentisera",
    cancel: "Avbryt",
  },
  form: {
    title: "Kundrespons",
    guideText:
      "Om du vill ha svar på din respons måste du autentisera dig i avsnittet <boldItalic>Jag vill ha svar på min respons</boldItalic>.",
    lead: mandatoryFieldsText.se,
    feedbackInfo: "Information om respons",
    contactInfo: "Kontaktinformation",
    givingFeedback: "Jag ger respons:",
    onBehalfOfName: "Personens namn:",
    onBehalfOfNamePlaceholder: "Ange personens namn",
    myFeedback: "Min respons är:",
    name: "Namn:",
    namePlaceholder: "Ange ditt namn",
    email: "E-post:",
    emailPlaceholder: "Ange din e-postadress",
    phone: "Telefon:",
    phonePlaceholder: "Ange ditt telefonnummer (t.ex. 0401234567)",
    iam: "Jag är:",

    employer: "Arbetsgivare:",
    subject: "Ärendet gäller:",
    subjectOther: "Annat, vad:",
    service: "Precisera genom att välja den tjänst som responsen gäller:",
    dateOfOccurrence: "Datum för händelsen:",
    selectDate: "Välj datum",
    describeMatter: "Berätta mer om ärendet:",
    feedback: "Respons / meddelande:",
    replyWish: "Jag vill ha svar på min respons:",
    preferredContactMethod: "Hur vill du få ditt svar:",
    toimipaikka: "Plats som responsen gäller:",
    toimipaikkaSelect: "Välj plats...",
    toimipaikkaPlaceholder: "Skriv för att söka platser...",
    toimipaikkaHelp: [
      "Om din respons gäller Tidsbeställning eller Kundservice, välj {{appointmentBooking}}.",
      "Om din respons gäller Terveystalos distanstjänster (t.ex. läkar-chatt), välj {{digitalHealth}}.",
      "Om din respons gäller Digihoitajapalvelu (t.ex. sjuksköterske- och mental hälsa-chatt), välj {{nurseTeams}}.",
      "Om din respons gäller Terveystalo i allmänhet, välj {{general}}.",
    ],
    privacyNotice: "Dina personuppgifter sparas av Terveystalo, där de behandlas i enlighet med ",
    privacyStatement: "dataskyddsbeskrivningen för privatkunder",
    privacyStatementUrl: "https://www.terveystalo.com/fi/tietosuojaseloste/henkiloasiakkaat",
    privacyCustomers: ".",
    submitting: "Sändning...",
    attachments: "Lägg till bilaga här:",
    addAttachment: "Lägg till bilaga",
    filesSelected: "{{count}} fil(er) valda",
    fileUploadHint: "Max bilagsstorlek 20 MB",
    loading: "Laddar...",
  },
  auth: {
    error: "Autentisering misslyckades. Försök igen.",
    processing: "Autentiserar...",
  },
  invoicingForm: {
    title: "Privatkunds fakturerings- och responsformulär",
    lead: mandatoryFieldsText.se,
    formInfo: "Information om respons",
    typeLabel: "Typ:",
    typeInstructions:
      "Om du har en fråga eller vill ge respons om en mottagen faktura eller betalning, välj Respons typ. Om du vill ha allmän rådgivning om fakturering och betalning, välj Förfrågan typ.",
    descriptionLabel: "Berätta mer om ärendet (respons/meddelande):",
    visitDateLabel: "Besöksdatum:",
    invoiceNumberLabel: "Fakturanummer:",
    emailLabel: "E-postadress:",
    type: {
      select: "Välj typ av respons",
      feedback: "Respons",
      thank_you: "Tack",
      inquiry: "Förfrågan",
    },
  },
  organizationForm: {
    title: "Respons från företag och organisationer",
    lead: mandatoryFieldsText.se,
    formInfo: "Organisationsinformation",
    feedbackInfo: "Information om respons",
    organizationTypeLabel: "Organisationstyp:",
    organizationNameLabel: "Organisationsnamn:",
    businessIdLabel: "FO-nummer:",
    businessIdPlaceholder: "Ange FO-nummer i format 1234567-8",
    customerRelationshipLabel: "Typ av kundrelation:",
    subjectLabel: "Välj ämne som du vill ge respons om:",
    invoiceNumberLabel: "Fakturanummer:",
    menuItemLabel:
      "Välj en menypost för att specificera var saken inträffade eller vad den är relaterad till:",
    dateOfOccurrenceLabel: "Datum för händelsen:",
    descriptionLabel: "Berätta mer om ärendet (respons/meddelande):",
    wishToReceiveResponseLabel: "Jag vill ha svar på min respons:",
    toimipaikkaLabel: "Välj den plats din respons gäller:",
    toimipaikkaSelect: "Välj plats...",
    toimipaikkaPlaceholder: "Skriv för att söka platser...",
    nameLabel: "Mitt namn är:",
    namePlaceholder: "Ange ditt namn",
    emailLabel: "E-postadress:",
    organizationType: {
      select: "Välj organisationstyp...",
      municipality: "Kommun",
      other: "Annat",
      wellbeing_area: "Välfärdsområde",
      insurance_company: "Försäkringsbolag",
      community: "Gemenskap",
      company: "Företag",
    },
    customerRelationship: {
      select: "Välj kundrelation...",
      hansel_occupational: "Hansel företagshälsovårdskund",
      public_sector: "Kund från en organisation inom offentliga sektorn eller ett samfund",
      local_occupational: "Lokal företagshälsovårdskund",
      sickness_fund: "Sjukkassa eller försäkringskassa",
      insurance: "Försäkringskund",
      national_occupational: "Riksomfattande företagshälsovårdskund",
    },
    subject: {
      select: "Välj ämne...",
      appointment_booking: "Tidsbeställning",
      customer_experience: "Kundupplevelse",
      customer_relationship_management: "Kundrelationshantering",
      remote_consultation: "Distanstjänster (chatt, videomottagning, telefonmottagning)",
      extranet_service: "Extranettjänst",
      direct_access_physical_therapist: "Fysioterapeutens direktmottagning",
      pricing: "Prissättning",
      treatment_and_examinations: "Vård och undersökning",
      invoicing: "Fakturering",
      medical_applications: "Medicinska tillämpningar (Hälsoenkäter Sante/Etydi)",
      marketing_and_communication: "Marknadsföring och kommunikation",
      other: "Annat",
      sales: "Försäljning",
      supply_of_services: "Tillhandahållande av tjänster",
      sirius: "Sirius",
      agreement_issues: "Avtalsfrågor",
      electronically_submitted_employee_info: "Elektroniskt inlämnad anställningsinformation",
      oma_terveys_app: "Oma Terveys/ Terveystalo-appen",
      referrals_made_at_terveystalo: "Remisser gjorda på Terveystalo",
      data_protection: "Integritetsskydd",
      website: "Webbplatsen www.terveystalo.com",
      environmental_responsibility: "Miljöansvar",
    },
    menuItem: {
      select: "Välj menypost...",
      customer_service_desk: "Service vid kundservicedisken",
      laboratory_examinations: "Laboratorieundersökningar",
      surgery_and_operations: "Kirurgi och operationer",
      imaging_examinations: "Bildundersökningar",
      screening: "Screening",
      oral_health: "Munhälsa",
      partner_service: "Service som tillhandahålls av Terveystalos partner",
      appointment: "Besök (läkare, sjuksköterska, fysioterapeut etc.)",
      video_consultation: "Videokonsultation",
      common: "Gemensam",
    },
  },
  vaaratapahtumaForm: {
    title: "Patientens odesliga händelse",
    lead: mandatoryFieldsText.se,
    formInfo: "Händelseinformation",
    descriptionLabel: "Berätta så noggrant som möjligt vad som hände och vilka konsekvenserna var:",
    toimipaikkaLabel: "Välj den plats som ärendet gäller:",
    toimipaikkaSelect: "Välj plats...",
    toimipaikkaPlaceholder: "Skriv för att söka platser...",
    dateOfOccurrenceLabel: "Datum för händelsen:",
    emailLabel: "E-postadress:",
  },
  korjauspyyntoForm: {
    title: "Korrigeringsbegäran för personuppgifter",
    lead: mandatoryFieldsText.se,
    formInfo: "Begäraninformation",
    gdprNotice:
      "Baserat på EU:s allmänna dataskyddsförordning (EU 2016/679) begär jag att felaktiga och inkorrekta personuppgifter om mig (den registrerade) som lagras i ert personregister för personkunder korrigeras och/eller att onödiga personuppgifter raderas.",
    notAuthenticatedMessage:
      "Du måste vara inloggad för att skicka en korrigeringsbegäran för personuppgifter.",
    printModeEnabled: "Utskrivbart formulärläge",
    printModeBanner:
      "Detta formulär kommer att skrivas ut. Fyll i alla obligatoriska fält, skriv ut, signera och lämna in personligen.",
    printButton: "Skriv ut formulär",
    printInstructions:
      "Skriv ut detta formulär, signera det och lämna in det till en Terveystalo-plats med giltig identifikation.",
    usePrintableForm: "Använd utskrivbart formulär",
    printModeExplanation:
      "Om du inte vill logga in kan du använda det utskrivbara formuläret. Fyll i formuläret, skriv ut det, signera det och lämna in det till en Terveystalo-plats.",
    authenticatedExplanation:
      "När du är inloggad kan du skicka din korrigeringsbegäran omedelbart.",
    firstAndLastName: "För- och efternamn:",
    personalIdentityCode: "Personbeteckning:",
    dateField: "Datum:",
    signature: "Underskrift:",
    employeeSignature: "Terveystalo-medarbetarens underskrift och namnförtydligande:",
    identityVerificationTitle:
      "Begärans identitet verifierad (Att fyllas i av Terveystalo-representant)",
    identityTypePassport: "Giltigt pass",
    identityTypeDriversLicense: "Finskt körkort",
    identityTypeIdCard: "Officiellt ID-kort (utfärdat av polisen)",
    identityTypeOther: "Annat, vad:",
    dateReceived: "Datum mottaget:",
    decision: "Beslut (Att fyllas i av Terveystalo-representant):",
    submissionInstructions:
      "Lämna in detta formulär till vilken Terveystalo-plats som helst med giltig identifikation.",
    toimipaikkaLabel: "Plats som begäran gäller:",
    toimipaikkaSelect: "Välj plats...",
    toimipaikkaPlaceholder: "Skriv för att söka platser...",
    requestTypesLabel: "Jag begär att följande information:",
    requestTypePoistettaviksi: "Raderas",
    requestTypeMuutettaviksi: "Ändras",
    requestTypeLisattaviksi: "Läggs till",
    requestTypeOstopalveluasiakkaana:
      "Korrigeringsbegäran gäller mina besök hos Terveystalo som köpt tjänst-kund (t.ex. välfärdsområdets, arbetsgivarens egen företagshälsovård eller annan vårdgivares betalningsförbindelse, servicesedel eller köpt tjänst-fullmakt).",
    descriptionPoistettaviksiLabel: "Beskriv den information som ska raderas:",
    descriptionMuutettaviksiLabel: "Beskriv den information som ska ändras:",
    descriptionLisattaviksiLabel: "Beskriv den information som ska läggas till:",
    descriptionOstopalveluasiakkaanaLabel: "Beskriv ärendet gällande köpta tjänster:",
    addressLabel: "Adress:",
    phoneNumberLabel: "Telefonnummer:",
    emailLabel: "E-postadress:",
    dateOfRequestLabel: "Datum för begäran:",
  },
  muistutusForm: {
    title: "Anmärkning enligt patientlagen",
    lead: mandatoryFieldsText.se,
    formInfo: "Anmärkningsinformation",
    authenticatedExplanation: "När du är inloggad kan du skicka din anmärkning omedelbart.",
    notAuthenticatedMessage:
      "Du måste vara inloggad för att skicka en anmärkning enligt patientlagen.",
    printModeExplanation:
      "Om du inte vill logga in kan du använda det utskrivbara formuläret. Fyll i formuläret, skriv ut det, signera det och lämna in det till en Terveystalo-plats.",
    usePrintableForm: "Använd utskrivbart formulär",
    printModeBanner:
      "Detta formulär skrivs ut. Fyll i alla obligatoriska fält, skriv ut, signera och lämna in personligen.",
    printInstructions:
      "Skriv ut detta formulär, signera det och lämna in det till en Terveystalo-plats med giltig legitimation.",
    printButton: "Skriv ut formuläret",
    onBehalfOfLabel: "Agerar du på en annan persons vägnar?",
    onBehalfOfYes: "Ja",
    onBehalfOfNo: "Nej",
    onBehalfOfInfoTitle: "Agera på en annan persons vägnar",
    onBehalfOfInfoText: "Kom ihåg att bifoga en fullmakt för ombud.",
    readMore: "Läs mer:",
    onBehalfOfInfoLink: "Agera på en annan persons vägnar | Terveystalo",
    onBehalfOfInfoUrl:
      "https://www.terveystalo.com/fi/asiakaspalvelu/asiointiohjeet/toisen-puolesta-asiointi",
    patientInfoTitle: "Patientinformation",
    patientNameLabel: "Namn:",
    patientNamePlaceholder: "Ange patientens namn",
    patientSsnLabel: "Personbeteckning:",
    patientSsnPlaceholder: "Ange patientens personbeteckning",
    patientPhoneLabel: "Telefonnummer:",
    patientPhonePlaceholder: "Ange patientens telefonnummer",
    patientEmailLabel: "E-post:",
    patientEmailPlaceholder: "Ange patientens e-postadress",
    patientAddressLabel: "Postadress:",
    patientAddressPlaceholder: "Ange patientens postadress",
    purchaseServiceCustomerLabel:
      "Anmärkningen gäller mitt besök hos Terveystalo som köpt tjänst-kund (t.ex. välfärdsområdets, arbetsgivarens egen företagshälsovård eller annan vårdgivares betalningsförbindelse, servicesedel eller köpt tjänst-fullmakt).",
    representativeInfoTitle: "Ombud information",
    representativeNameLabel: "Namn:",
    representativeNamePlaceholder: "Ange ombud namn",
    representativeSsnLabel: "Personbeteckning:",
    representativeSsnPlaceholder: "Ange ombud personbeteckning",
    representativePhoneLabel: "Telefonnummer:",
    representativePhonePlaceholder: "Ange ombud telefonnummer",
    representativeEmailLabel: "E-post:",
    representativeEmailPlaceholder: "Ange ombud e-postadress",
    representativeAddressLabel: "Postadress:",
    representativeAddressPlaceholder: "Ange ombud postadress",
    reminderDetailsTitle: "Anmärkningsuppgifter",
    toimipaikkaLabel: "Plats:",
    toimipaikkaSelect: "Välj plats...",
    toimipaikkaPlaceholder: "Skriv för att söka platser...",
    muistutusLabel: "Anmärkning:",
    muistutusPlaceholder:
      "Ange anmärkningens detaljer i textfältet eller bifoga som en separat bilaga.",
    responseMethodTitle: "Hur vill du få svaret:",
    responseMethodEncryptedEmail: "Levereras med krypterad e-post",
    responseMethodPost: "Levereras per post",
    responseMethodApp: "Levereras till Terveystalo-appen/webbtjänsten (gäller inte munhälsa)",
    responseMethodAppDisabled: "Inte tillgängligt vid ombud",
  },
}

const resources = {
  en: { translation: enTranslation },
  fi: { translation: fiTranslation },
  se: { translation: seTranslation },
}

// Detect initial language from URL or localStorage
const getInitialLanguage = (): string => {
  // Check URL pathname
  const pathParts = window.location.pathname.split("/").filter(Boolean)
  const langFromUrl = pathParts[0]
  const supportedLanguages = ["en", "fi", "se"]

  if (langFromUrl && supportedLanguages.includes(langFromUrl)) {
    return langFromUrl
  }

  // Check localStorage
  const savedLang = localStorage.getItem("preferredLanguage")
  if (savedLang && supportedLanguages.includes(savedLang)) {
    return savedLang
  }

  // Check browser language
  const browserLang = navigator.language.split("-")[0].toLowerCase()
  if (supportedLanguages.includes(browserLang)) {
    return browserLang
  }

  // Default to English
  return "en"
}

i18n.use(initReactI18next).init({
  resources,
  lng: getInitialLanguage(), // Initialize with language from URL
  fallbackLng: "en",
  interpolation: {
    escapeValue: false, // React already escapes values
  },
  react: {
    useSuspense: false,
    bindI18n: "languageChanged",
    bindI18nStore: "",
  },
})

export default i18n
